<template>
    <div class="flex flex-col gap-3.5 w-full">
        <div class="flex flex-col md:flex-row md:flex-wrap md:items-center xl:flex-nowrap xl:justify-between w-full gap-3.5">
            <div class="md:mr-4 body-small text-valence-grey-750">Date&nbsp;Range</div>
            <BaseSelect v-model="selectedRange" icon="bi-calendar mr-1" :options="dateRangeOptions" />
        </div>
        <div v-if="selectedRange === CUSTOM_RANGE_KEY" class="flex flex-col gap-1.5">
            <div class="flex flex-col md:flex-row md:items-center w-full gap-3.5">
                <label :for="startDateInputId" class="flex-1 flex items-center gap-3.5 body-small text-valence-grey-750">
                    From
                    <input :id="startDateInputId" v-model="customStartDate" type="date" :max="customEndDate || today" class="date-input" />
                </label>
                <label :for="endDateInputId" class="flex-1 flex items-center gap-3.5 body-small text-valence-grey-750">
                    To
                    <input :id="endDateInputId" v-model="customEndDate" type="date" :min="customStartDate || undefined" class="date-input" />
                </label>
            </div>
            <div v-if="customRangeError" class="body-small text-valence-coral-800" role="alert">{{ customRangeError }}</div>
        </div>
    </div>
</template>

<script lang="ts">
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";

import { enqueueURLParamsUpdate } from "../../utils/enqueueURLParamsUpdate";
import type { DateFilters } from "../types";

export const DEFAULT_DATE_FILTERS: DateFilters = { startDate: null, endDate: null } as const;
</script>

<script setup lang="ts">
import BaseSelect from "~vue/components/BaseSelect.vue";
import { DateTime, type DurationLike } from "luxon";
import { computed, ref, useId, watch } from "vue";

const ALL_TIME_RANGE_KEY = "allTime";
const CUSTOM_RANGE_KEY = "custom";

const PRESET_RANGE_DURATIONS: Record<string, DurationLike> = {
    last30Days: { days: 30 },
    last90Days: { days: 90 },
    last6Months: { months: 6 },
    last12Months: { months: 12 },
};

const dateRangeOptions = [
    { label: "All time", key: ALL_TIME_RANGE_KEY },
    { label: "Last 30 days", key: "last30Days" },
    { label: "Last 90 days", key: "last90Days" },
    { label: "Last 6 months", key: "last6Months" },
    { label: "Last 12 months", key: "last12Months" },
    { label: "Custom", key: CUSTOM_RANGE_KEY },
];

const getPresetStartDate = (rangeKey: string) => {
    const duration = PRESET_RANGE_DURATIONS[rangeKey];
    return duration ? DateTime.local().minus(duration).toISODate() : null;
};

const parseURLDateParam = (value: string | null) => {
    const parsed = DateTime.fromISO(value ?? "");
    return parsed.isValid ? parsed.toISODate() : null;
};

const today = DateTime.local().toISODate();

const startDateInputId = useId();
const endDateInputId = useId();

const urlParams = new URLSearchParams(globalThis.location.search);
const urlStartDate = parseURLDateParam(urlParams.get("startDate"));
const urlEndDate = parseURLDateParam(urlParams.get("endDate"));
const matchingPresetRange = urlEndDate ? undefined : Object.keys(PRESET_RANGE_DURATIONS).find((key) => getPresetStartDate(key) === urlStartDate);

const selectedRange = ref(matchingPresetRange ?? (urlStartDate || urlEndDate ? CUSTOM_RANGE_KEY : ALL_TIME_RANGE_KEY));
const customStartDate = ref(urlStartDate);
const customEndDate = ref(urlEndDate ?? (urlStartDate ? today : null));

const customRangeError = computed(() => {
    if (!customStartDate.value || !customEndDate.value || customEndDate.value >= customStartDate.value) {
        return null;
    }
    return "End date must be on or after the start date.";
});

const selectedFilters = computed<DateFilters>(() => {
    if (selectedRange.value === CUSTOM_RANGE_KEY) {
        return { startDate: customStartDate.value || null, endDate: customEndDate.value || null };
    }
    return { startDate: getPresetStartDate(selectedRange.value), endDate: null };
});

const emit = defineEmits<{
    updated: [filters: DateFilters];
}>();

watch(selectedRange, (newRange) => {
    if (newRange === CUSTOM_RANGE_KEY && !customEndDate.value) {
        customEndDate.value = today;
    }
});

// Emit event and update URL search params when selected filters change
watch(
    selectedFilters,
    (newSelectedFilters) => {
        if (customRangeError.value) {
            return;
        }

        emit("updated", newSelectedFilters);

        enqueueURLParamsUpdate((currentParams) => {
            for (const [param, value] of [
                ["startDate", newSelectedFilters.startDate],
                ["endDate", newSelectedFilters.endDate],
            ] as const) {
                if (value) {
                    currentParams.set(param, value);
                } else {
                    currentParams.delete(param);
                }
            }
            return currentParams;
        });
    },
    { immediate: true },
);

watch(selectedFilters, (newSelectedFilters) => {
    if (customRangeError.value) {
        return;
    }

    logUserInteraction(AmplitudeEventNames.CLIENT_ANALYTICS_DATE_FILTERS_SELECTED, {
        start_date: newSelectedFilters.startDate,
        end_date: newSelectedFilters.endDate,
    });
});

// Expose reset function for parent to call when clearing filters
const reset = () => {
    selectedRange.value = ALL_TIME_RANGE_KEY;
    customStartDate.value = null;
    customEndDate.value = null;
};

defineExpose({
    reset,
});
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .date-input {
        @apply flex-1 min-w-0 rounded-lg p-3 border border-valence-grey-300 body-small text-valence-grey-750 bg-white;
    }
}
</style>
